import { useCallback, useMemo } from 'react';
import { showInfo } from '../../../shared/ui/appToast';
import { isInventoriableProduct } from '../../../inventory/stock/public-api';
import { countPhysicalCountInvalidRows } from '../../application/physicalCountCore';
import { useProductVariantsEnabled } from '../../../catalog/app-api';
import { DEFAULT_BULK_CATEGORY_SCOPE, DEFAULT_BULK_PRICE_ROUNDING } from '../../../catalog/public-api';
import { useInventoryUiStore } from '../../store/index';
import { applyInventoryUserNotice, handleInventoryFlowResult, isInventoryFlowDone } from '../../application/inventoryFlowBridge';
import type { InventoryRefreshHandlers } from '../../application/inventoryFlowBridge';
import { useInventoryOperationsController } from './useInventoryOperationsController';

type Params = {
  ctx: { currentBranchId: string | null | undefined; user: { id?: string } | null | undefined; canEdit: boolean; canCount?: boolean };
  refresh: InventoryRefreshHandlers;
};

export function useInventoryBulkPhysicalFlow({ ctx, refresh }: Params) {
  const controller = useInventoryOperationsController();
  const variantsEnabled = useProductVariantsEnabled();
  const branchId = ctx.currentBranchId ?? null;
  const editCtx = useMemo(
    () => ({ canEdit: ctx.canEdit, canCount: ctx.canCount, branchId, userId: ctx.user?.id ?? null }),
    [ctx.canEdit, ctx.canCount, branchId, ctx.user?.id]
  );

  const {
    bulkPercentInput,
    bulkCategoryScope,
    bulkSearch,
    bulkApplyTo,
    bulkPriceRounding,
    bulkPreview,
    setBulkPreview,
    setBulkApplyTotal,
    setBulkPercentInput,
    setBulkCategoryScope,
    setBulkSearch,
    setBulkApplyTo,
    setBulkCoeffVisible,
    setBulkScannerVisible,
    setBulkPreviewLoading,
    setBulkSaving,
    setBulkSaveProgress,
    physicalCountRows,
    setPhysicalCountVisible,
    setPhysicalCountLoading,
    setPhysicalCountSaving,
    setPhysicalCountRows,
  } = useInventoryUiStore();

  const openBulkCoeffModal = useCallback(() => {
    const gate = controller.canOpenBulk({ canEdit: ctx.canEdit });
    if (gate.kind === 'blocked') {
      applyInventoryUserNotice(gate.notice);
      return;
    }
    setBulkPreview([]);
    setBulkApplyTotal(0);
    setBulkPercentInput('');
    setBulkCategoryScope(DEFAULT_BULK_CATEGORY_SCOPE);
    setBulkSearch('');
    setBulkApplyTo('prices');
    useInventoryUiStore.getState().setBulkPriceRounding(DEFAULT_BULK_PRICE_ROUNDING);
    setBulkCoeffVisible(true);
  }, [
    controller,
    ctx.canEdit,
    setBulkApplyTo,
    setBulkApplyTotal,
    setBulkCategoryScope,
    setBulkCoeffVisible,
    setBulkPercentInput,
    setBulkPreview,
    setBulkSearch,
  ]);

  const handleBulkScan = useCallback(
    async (rawCode: string) => {
      setBulkScannerVisible(false);
      try {
        const result = await controller.lookupBarcode(rawCode);
        setBulkSearch(result.barcode || rawCode);
        if (result.kind === 'local_hit' && !isInventoriableProduct(result.product)) {
          showInfo('Servicio sin stock', 'Los servicios no se cuentan en Stock.');
        }
      } catch {
        setBulkSearch(rawCode);
      }
    },
    [controller, setBulkScannerVisible, setBulkSearch]
  );

  const loadBulkPreview = useCallback(async () => {
    setBulkPreviewLoading(true);
    try {
      const result = await controller.loadBulkPreview(editCtx, {
        bulkCategoryScope,
        bulkSearch,
        bulkPercentInput,
        bulkApplyTo,
        bulkPriceRounding,
      });
      const handled = await handleInventoryFlowResult(result, refresh);
      if (isInventoryFlowDone(handled) && handled.data && typeof handled.data === 'object' && 'items' in handled.data) {
        setBulkPreview(handled.data.items);
        setBulkApplyTotal(handled.data.applyTotal);
      }
    } finally {
      setBulkPreviewLoading(false);
    }
  }, [
    bulkApplyTo,
    bulkCategoryScope,
    bulkPercentInput,
    bulkPriceRounding,
    bulkSearch,
    controller,
    editCtx,
    refresh,
    setBulkApplyTotal,
    setBulkPreview,
    setBulkPreviewLoading,
  ]);

  const applyBulkCoefficient = useCallback(async () => {
    if (bulkPreview.length === 0) return;
    if (useInventoryUiStore.getState().bulkSaving) return;
    setBulkSaving(true);
    setBulkSaveProgress(null);
    try {
      const result = await controller.runBulkApply(editCtx, {
        bulkCategoryScope,
        bulkSearch,
        bulkPercentInput,
        bulkApplyTo,
        bulkPriceRounding,
        preview: bulkPreview,
        onApplyProgress: (p) => setBulkSaveProgress(p),
      });
      const handled = await handleInventoryFlowResult(result, refresh);
      if (isInventoryFlowDone(handled)) {
        setBulkCoeffVisible(false);
        setBulkPreview([]);
        setBulkApplyTotal(0);
      }
    } finally {
      setBulkSaving(false);
      setBulkSaveProgress(null);
    }
  }, [
    bulkApplyTo,
    bulkCategoryScope,
    bulkPercentInput,
    bulkPriceRounding,
    bulkPreview,
    bulkSearch,
    controller,
    editCtx,
    refresh,
    setBulkApplyTotal,
    setBulkCoeffVisible,
    setBulkPreview,
    setBulkSaveProgress,
    setBulkSaving,
  ]);

  const resolvePhysicalCountScanFilter = useCallback(async (rawCode: string): Promise<string | null> => {
    const result = await controller.lookupBarcode(rawCode);
    if (result.kind === 'local_hit' && isInventoriableProduct(result.product)) {
      return result.product.sku ?? result.product.name;
    }
    if (result.kind === 'local_hit') showInfo('Servicio sin stock', 'Los servicios no entran en el conteo.');
    return null;
  }, [controller]);

  const openPhysicalCountModal = useCallback(async () => {
    const gate = controller.canOpenPhysicalCount(editCtx);
    if (gate.kind === 'blocked') {
      applyInventoryUserNotice(gate.notice);
      return;
    }
    setPhysicalCountVisible(true);
    setPhysicalCountLoading(true);
    try {
      const result = await controller.loadPhysicalCountSession(editCtx, { variantsEnabled });
      const handled = await handleInventoryFlowResult(result, refresh);
      if (isInventoryFlowDone(handled) && Array.isArray(handled.data)) {
        setPhysicalCountRows(handled.data);
      } else {
        setPhysicalCountVisible(false);
      }
    } finally {
      setPhysicalCountLoading(false);
    }
  }, [controller, editCtx, refresh, setPhysicalCountLoading, setPhysicalCountRows, setPhysicalCountVisible, variantsEnabled]);

  const setPhysicalCountedQty = useCallback(
    (stockId: string, value: string) => {
      setPhysicalCountRows((prev) => prev.map((row) => (row.id === stockId ? { ...row, countedQty: value } : row)));
    },
    [setPhysicalCountRows]
  );

  const applyPhysicalCountDifferences = useCallback(async () => {
    if (useInventoryUiStore.getState().physicalCountSaving) return;
    const invalidCount = countPhysicalCountInvalidRows(physicalCountRows);
    setPhysicalCountSaving(true);
    try {
      const result = await controller.runPhysicalCountApply(editCtx, physicalCountRows);
      const handled = await handleInventoryFlowResult(result, refresh);
      if (isInventoryFlowDone(handled)) {
        if (invalidCount > 0) {
          showInfo(
            'Filas sin aplicar',
            `${invalidCount} fila${invalidCount === 1 ? '' : 's'} con valor inválido no se ${invalidCount === 1 ? 'aplicó' : 'aplicaron'}.`
          );
        }
        setPhysicalCountVisible(false);
        setPhysicalCountRows([]);
      }
    } finally {
      setPhysicalCountSaving(false);
    }
  }, [controller, editCtx, physicalCountRows, refresh, setPhysicalCountRows, setPhysicalCountSaving, setPhysicalCountVisible]);

  return {
    openBulkCoeffModal,
    handleBulkScan,
    loadBulkPreview,
    applyBulkCoefficient,
    openPhysicalCountModal,
    resolvePhysicalCountScanFilter,
    setPhysicalCountedQty,
    applyPhysicalCountDifferences,
  };
}
